// Dashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import MapboxMap, { Marker, NavigationControl } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import "./Dashboard.css";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const MAP_PREVIEW_CHIP_LIMIT = 4;

const emptyDashboardData = {
  capabilities: {
    accountBalance: false,
    openQuotes: false,
  },
  summary: {
    accountBalance: null,
    openQuotes: null,
    openWorkOrders: 0,
    maintenanceStatus: 0,
    activeAssets: 0,
  },
  map: {
    properties: [],
  },
  tables: {
    openWorkOrders: [],
    openQuotes: [],
    maintenanceStatus: [],
  },
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);
};

const formatDate = (value) => {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleDateString();
};

const getStatusTone = (status) => {
  const normalized = String(status || "").toUpperCase();

  if (normalized === "SCHEDULED") return "scheduled";
  if (normalized === "UNSCHEDULED") return "open";

  return "neutral";
};

const getValidMapProperties = (properties = []) =>
  properties.filter(
    (property) =>
      Number.isFinite(Number(property?.longitude)) &&
      Number.isFinite(Number(property?.latitude))
  );

const getBounds = (properties = []) => {
  const lngs = properties.map((property) => Number(property.longitude));
  const lats = properties.map((property) => Number(property.latitude));

  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
};

const getRowKey = (row, rowIndex, title) =>
  row.jobId ||
  row.quoteId ||
  row.workOrder ||
  row.quoteNumber ||
  row.serviceAgreement ||
  `${title}-${rowIndex}`;

const buildOpenWorkOrderColumns = () => [
  {
    key: "workOrder",
    label: "Work Order",
    render: (row) => (
      <div className="dashboard-primary-cell">
        {!row.workOrder ? (
          "—"
        ) : row.reportUrl ? (
          <a
            href={row.reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-primary-link"
          >
            {row.workOrder}
          </a>
        ) : (
          row.workOrder
        )}
      </div>
    ),
  },
  { key: "description", label: "Description" },
  { key: "type", label: "Type" },
  {
    key: "status",
    label: "Status",
    render: (row) => (
      <span
        className={`dashboard-status-pill dashboard-status-pill--${getStatusTone(
          row.status
        )}`}
      >
        {row.status || "—"}
      </span>
    ),
  },
  { key: "property", label: "Property" },
];

const buildMaintenanceColumns = () => [
  {
    key: "workOrder",
    label: "Maintenance",
    render: (row) => (
      <div className="dashboard-primary-cell">
        {row.workOrder && row.reportUrl ? (
          <a
            href={row.reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-primary-link"
          >
            {row.workOrder}
          </a>
        ) : (
          row.workOrder || row.serviceAgreement || "—"
        )}
      </div>
    ),
  },
  { key: "description", label: "Description" },
  { key: "type", label: "Type" },
  {
    key: "status",
    label: "Status",
    render: (row) => (
      <span
        className={`dashboard-status-pill dashboard-status-pill--${getStatusTone(
          row.status
        )}`}
      >
        {row.status || "—"}
      </span>
    ),
  },
  { key: "property", label: "Property" },
  {
    key: "maintenanceDueDate",
    label: "Due Date",
    render: (row) => formatDate(row.maintenanceDueDate),
  },
];

const buildQuoteColumns = () => [
  {
    key: "quoteNumber",
    label: "Quote",
    render: (row) => (
      <div className="dashboard-primary-cell">{row.quoteNumber || "—"}</div>
    ),
  },
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
  {
    key: "amount",
    label: "Quote Amount",
    render: (row) => formatCurrency(row.amount),
  },
  { key: "property", label: "Property" },
];

/* -------------------------------------------------------------------------- */
/*                              Reusable UI Pieces                            */
/* -------------------------------------------------------------------------- */

const SkeletonRows = ({ cols, count = 5 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j}><span className="skeleton-line" /></td>
      ))}
    </tr>
  ));

const SummaryCard = ({ label, value, accent = "default", isLoading }) => {
  return (
    <div className={`dashboard-summary-card dashboard-summary-card--${accent}`}>
      <div className="dashboard-summary-card__label">{label}</div>
      <div className="dashboard-summary-card__value">
        {isLoading ? <span className="card-spinner" /> : value}
      </div>
    </div>
  );
};

const DashboardCardHeader = ({ title, meta }) => {
  return (
    <div className="dashboard-card__header">
      <h3 className="dashboard-card__title">{title}</h3>
      <span className="dashboard-card__meta">{meta}</span>
    </div>
  );
};

const TableCard = ({
  title,
  columns,
  rows,
  emptyMessage = "No data",
  cardClassName = "",
  tableWrapClassName = "",
  isLoading = false,
}) => {
  const hasRows = Array.isArray(rows) && rows.length > 0;

  return (
    <section className={`dashboard-card dashboard-table-card ${cardClassName}`}>
      <DashboardCardHeader title={title} meta={`${rows?.length || 0} items`} />

      {isLoading || hasRows ? (
        <div className={`dashboard-table-wrap ${tableWrapClassName}`}>
          <table className="dashboard-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {isLoading ? <SkeletonRows cols={columns.length} /> : rows.map((row, rowIndex) => (
                <tr
                  key={getRowKey(row, rowIndex, title)}
                  className={rowIndex % 2 === 0 ? "" : "is-alt"}
                >
                  {columns.map((column) => (
                    <td key={column.key}>
                      {column.render
                        ? column.render(row)
                        : row[column.key] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="dashboard-empty-state">{emptyMessage}</div>
      )}
    </section>
  );
};

const CompanySelect = ({ customers, selectedCustomerId, setSelectedCustomerId, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selected = customers.find((c) => String(c.id) === String(selectedCustomerId));
  const buttonLabel = selected ? selected.name : "All Companies";

  return (
    <div ref={wrapperRef} className="dashboard-filter">
      <label className="dashboard-filter__label">Company</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="dashboard-select-button"
      >
        <span className="dashboard-select-button__text">{buttonLabel}</span>
        <span className={`dashboard-select-button__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>
      {open && !disabled ? (
        <div className="dashboard-multiselect-menu">
          <div className="dashboard-multiselect-list">
            <div
              className={`dashboard-checkbox-row${!selectedCustomerId ? " is-selected" : ""}`}
              onClick={() => { setSelectedCustomerId(""); setOpen(false); }}
            >
              <span>All Companies</span>
            </div>
            {customers.map((customer) => (
              <div
                key={customer.id}
                className={`dashboard-checkbox-row${String(selectedCustomerId) === String(customer.id) ? " is-selected" : ""}`}
                onClick={() => { setSelectedCustomerId(String(customer.id)); setOpen(false); }}
              >
                <span>{customer.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PropertyMultiSelect = ({
  properties,
  selectedPropertyIds,
  setSelectedPropertyIds,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selectedNames = properties
    .filter((property) => selectedPropertyIds.includes(String(property.id)))
    .map((property) => property.name);

  const buttonLabel = (() => {
    if (!selectedPropertyIds.length) return "All Properties";
    if (selectedPropertyIds.length === 1) {
      return selectedNames[0] || "1 Property";
    }

    return `${selectedPropertyIds.length} selected`;
  })();

  const toggleProperty = (propertyId) => {
    setSelectedPropertyIds((current) => {
      const stringId = String(propertyId);

      return current.includes(stringId)
        ? current.filter((id) => id !== stringId)
        : [...current, stringId];
    });
  };

  return (
    <div ref={wrapperRef} className="dashboard-filter dashboard-filter--wide">
      <label className="dashboard-filter__label">Properties</label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="dashboard-select-button"
      >
        <span className="dashboard-select-button__text">{buttonLabel}</span>
        <span
          className={`dashboard-select-button__chevron ${
            open ? "is-open" : ""
          }`}
        >
          ▾
        </span>
      </button>

      {open && !disabled ? (
        <div className="dashboard-multiselect-menu">
          <div className="dashboard-multiselect-actions">
            <button
              type="button"
              onClick={() => setSelectedPropertyIds([])}
              className="dashboard-secondary-button"
            >
              All
            </button>

            <button
              type="button"
              onClick={() =>
                setSelectedPropertyIds(
                  properties.map((property) => String(property.id))
                )
              }
              className="dashboard-secondary-button"
            >
              Select All Filtered
            </button>
          </div>

          <div className="dashboard-multiselect-list">
            {properties.length ? (
              properties.map((property) => {
                const checked = selectedPropertyIds.includes(
                  String(property.id)
                );

                return (
                  <label key={property.id} className="dashboard-checkbox-row">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleProperty(property.id)}
                    />
                    <span>{property.name}</span>
                  </label>
                );
              })
            ) : (
              <div className="dashboard-multiselect-empty">
                No properties available.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PropertyLocationsCard = ({ properties }) => {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  const mapRef = useRef(null);

  const validProperties = useMemo(
    () => getValidMapProperties(properties),
    [properties]
  );

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map || !validProperties.length) return;

    if (validProperties.length === 1) {
      const property = validProperties[0];

      map.flyTo({
        center: [Number(property.longitude), Number(property.latitude)],
        zoom: 13,
        duration: 0,
        essential: true,
      });

      return;
    }

    const [[minLng, minLat], [maxLng, maxLat]] = getBounds(validProperties);

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        padding: { top: 40, bottom: 40, left: 40, right: 40 },
        duration: 0,
        maxZoom: 14,
      }
    );
  }, [validProperties]);

  const hiddenPropertyCount = properties.length - validProperties.length;

  return (
    <section className="dashboard-card dashboard-map-card dashboard-feature-card">
      <DashboardCardHeader
        title="Property Coverage"
        meta={`${properties?.length || 0} properties`}
      />

      {!token ? (
        <div className="dashboard-map-empty dashboard-feature-panel">
          Add <code>REACT_APP_MAPBOX_TOKEN</code> to show the map.
        </div>
      ) : !validProperties.length ? (
        <div className="dashboard-map-empty dashboard-feature-panel">
          No valid property coordinates available.
        </div>
      ) : (
        <div className="dashboard-map-shell dashboard-feature-panel">
          <MapboxMap
            ref={mapRef}
            mapboxAccessToken={token}
            mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
            initialViewState={{
              longitude: Number(validProperties[0].longitude),
              latitude: Number(validProperties[0].latitude),
              zoom: validProperties.length === 1 ? 13 : 10,
            }}
            attributionControl={false}
            reuseMaps
            className="dashboard-map-live"
            style={{ width: "100%", height: "100%" }}
          >
            <NavigationControl position="top-right" />

            {validProperties.map((property) => (
              <Marker
                key={property.id}
                longitude={Number(property.longitude)}
                latitude={Number(property.latitude)}
                anchor="bottom"
              >
                <button
                  type="button"
                  className="dashboard-map-marker"
                  title={property.name}
                  aria-label={property.name}
                >
                  <span className="dashboard-map-marker__dot" />
                </button>
              </Marker>
            ))}
          </MapboxMap>
        </div>
      )}

      {hiddenPropertyCount > 0 ? (
        <div className="dashboard-map-note">
          {hiddenPropertyCount} propert
          {hiddenPropertyCount === 1 ? "y is" : "ies are"} missing coordinates
          and not shown on the map.
        </div>
      ) : null}

      <div className="dashboard-chip-list">
        {(properties || []).map((property) => (
          <span key={property.id} className="dashboard-chip">
            {property.name}
          </span>
        ))}
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*                                  Dashboard                                 */
/* -------------------------------------------------------------------------- */

const Dashboard = ({ viewerEmail }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  const [filterError, setFilterError] = useState("");
  const [dataError, setDataError] = useState("");

  const [dashboardData, setDashboardData] = useState(emptyDashboardData);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        setFiltersLoading(true);
        setFilterError("");

        const token = localStorage.getItem("token");
        if (!token) {
          setFilterError("No auth token found.");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/dashboard/dashboard-filters`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: viewerEmail ? { email: viewerEmail } : undefined,
          }
        );

        const data = response?.data || {};

        setCustomers(Array.isArray(data.customers) ? data.customers : []);
        setProperties(Array.isArray(data.properties) ? data.properties : []);
      } catch (error) {
        console.error("Failed to load dashboard filters:", error);
        setFilterError("Failed to load dashboard filters.");
      } finally {
        setFiltersLoading(false);
      }
    };

    loadFilters();
  }, [viewerEmail]);

  const filteredProperties = useMemo(() => {
    if (!selectedCustomerId) return properties;

    return properties.filter(
      (property) =>
        String(property.customerId ?? "") === String(selectedCustomerId)
    );
  }, [properties, selectedCustomerId]);

  useEffect(() => {
    const allowedIds = new Set(
      filteredProperties.map((property) => String(property.id))
    );

    setSelectedPropertyIds((current) =>
      current.filter((id) => allowedIds.has(String(id)))
    );
  }, [filteredProperties]);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (filtersLoading || filterError) return;

      try {
        setDataLoading(true);
        setDataError("");

        const token = localStorage.getItem("token");
        if (!token) {
          setDataError("No auth token found.");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/dashboard/dashboard-data`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              ...(viewerEmail ? { email: viewerEmail } : {}),
              ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
              ...(selectedPropertyIds.length
                ? { propertyIds: selectedPropertyIds.join(",") }
                : {}),
            },
          }
        );

        setDashboardData(response?.data || emptyDashboardData);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
        setDataError("Failed to load dashboard data.");
        setDashboardData(emptyDashboardData);
      } finally {
        setDataLoading(false);
      }
    };

    loadDashboardData();
  }, [
    filtersLoading,
    filterError,
    viewerEmail,
    selectedCustomerId,
    selectedPropertyIds,
  ]);

  const propertyLookup = useMemo(() => {
    const map = new Map();

    properties.forEach((property) => {
      map.set(String(property.id), property);
    });

    return map;
  }, [properties]);

  const displayedMapProperties = useMemo(() => {
    const rawProperties = Array.isArray(dashboardData?.map?.properties)
      ? dashboardData.map.properties
      : [];

    const mergedProperties = rawProperties.map((property) => {
      const lookup = propertyLookup.get(String(property.id)) || {};

      return {
        ...lookup,
        ...property,
      };
    });

    return mergedProperties.filter((property) => {
      if (
        selectedCustomerId &&
        String(property.customerId ?? "") !== String(selectedCustomerId)
      ) {
        return false;
      }

      if (
        selectedPropertyIds.length &&
        !selectedPropertyIds.includes(String(property.id))
      ) {
        return false;
      }

      return true;
    });
  }, [dashboardData, propertyLookup, selectedCustomerId, selectedPropertyIds]);

  const openWorkOrderColumns = useMemo(() => buildOpenWorkOrderColumns(), []);
  const maintenanceColumns = useMemo(() => buildMaintenanceColumns(), []);
  const quoteColumns = useMemo(() => buildQuoteColumns(), []);

  const isLoading = filtersLoading || dataLoading;

  return (
    <div className="dashboard-page">
      <div className="dashboard-shell">
        <section className="dashboard-toolbar">
          <div className="dashboard-toolbar__grid">
            <CompanySelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              disabled={filtersLoading || !!filterError}
            />

            <PropertyMultiSelect
              properties={filteredProperties}
              selectedPropertyIds={selectedPropertyIds}
              setSelectedPropertyIds={setSelectedPropertyIds}
              disabled={filtersLoading || !!filterError}
            />

            <div className="dashboard-toolbar__actions">
              <button
                type="button"
                onClick={() => {
                  setSelectedCustomerId("");
                  setSelectedPropertyIds([]);
                }}
                className="dashboard-clear-button"
                disabled={filtersLoading || !!filterError}
              >
                Clear
              </button>
            </div>
          </div>
        </section>


        {filterError ? (
          <div className="dashboard-feedback dashboard-feedback--error">
            {filterError}
          </div>
        ) : null}

        {dataError ? (
          <div className="dashboard-feedback dashboard-feedback--error">
            {dataError}
          </div>
        ) : null}

        {!filterError ? (
          <>
            <section className="dashboard-summary-grid dashboard-summary-grid--three">
              <SummaryCard
                label="Account Balance"
                value={
                  dashboardData.capabilities.accountBalance
                    ? formatCurrency(dashboardData.summary.accountBalance)
                    : "—"
                }
                accent="balance"
                isLoading={isLoading}
              />

              <SummaryCard
                label="Open Quotes"
                value={
                  dashboardData.capabilities.openQuotes
                    ? dashboardData.summary.openQuotes ?? 0
                    : "—"
                }
                accent="quotes"
                isLoading={isLoading}
              />

              <SummaryCard
                label="Open Work Orders"
                value={dashboardData.summary.openWorkOrders ?? 0}
                accent="workorders"
                isLoading={isLoading}
              />
            </section>

            <section className="dashboard-main-grid">
              <div className="dashboard-main-grid__primary">
                <TableCard
                  title="Open Work Orders"
                  columns={openWorkOrderColumns}
                  rows={dashboardData.tables.openWorkOrders}
                  emptyMessage="No open work orders"
                  cardClassName="dashboard-feature-card"
                  tableWrapClassName="dashboard-table-wrap--feature"
                  isLoading={isLoading}
                />
              </div>

              <div className="dashboard-main-grid__secondary">
                <PropertyLocationsCard properties={displayedMapProperties} />
              </div>
            </section>

            <section className="dashboard-secondary-grid">
              <TableCard
                title="Open Quotes"
                columns={quoteColumns}
                rows={dashboardData.tables.openQuotes}
                emptyMessage="No open quotes"
                isLoading={isLoading}
              />

              <TableCard
                title="Maintenance Status"
                columns={maintenanceColumns}
                rows={dashboardData.tables.maintenanceStatus}
                emptyMessage="No upcoming maintenance"
                isLoading={isLoading}
              />
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Dashboard;
